import React from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

function employeeSearchText(emp) {
  return [emp.full_name, emp.email, emp.mobile, emp.employee_code, emp.status]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * Searchable employee picker for staffing company HRMS screens.
 * value: candidate id (string | number) or '' when cleared
 * onChange: (candidateId) => void
 */
export default function StaffingEmployeeSelect({
  employees = [],
  value,
  onChange,
  label = 'Employee',
  placeholder = 'Type to search name, code, mobile…',
  showStatus = false,
  size = 'small',
  fullWidth = true,
  sx,
  disabled = false,
  required = false
}) {
  const selected = employees.find((e) => String(e.id) === String(value)) || null;

  return (
    <Autocomplete
      options={employees}
      value={selected}
      onChange={(_, emp) => onChange(emp ? emp.id : '')}
      disabled={disabled || employees.length === 0}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      autoHighlight
      openOnFocus
      clearOnEscape
      handleHomeEndKeys
      noOptionsText={employees.length === 0 ? 'No employees available' : 'No matching employees'}
      getOptionLabel={(emp) => emp.full_name || 'Unnamed'}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(options, { inputValue }) => {
        const q = inputValue.trim().toLowerCase();
        if (!q) return options;
        return options.filter((e) => employeeSearchText(e).includes(q));
      }}
      renderOption={(props, emp) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={key} {...optionProps} sx={{ py: 1.25, alignItems: 'flex-start !important' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {emp.full_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                {[emp.employee_code, emp.mobile, emp.email].filter(Boolean).join(' · ') || 'No contact on file'}
                {showStatus && emp.status ? ` · ${emp.status.replace(/_/g, ' ')}` : ''}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={employees.length ? placeholder : 'No employees available'}
          required={required}
          helperText={employees.length > 8 ? `${employees.length} employees — start typing to narrow the list` : undefined}
        />
      )}
      slotProps={{
        paper: {
          sx: { maxHeight: 360 }
        },
        listbox: {
          sx: { py: 0.5 }
        }
      }}
    />
  );
}
